import { useCallback, useRef } from 'react';

/**
 * Returns a ref callback that, when attached to a DOM element, fires `onView` exactly once
 * the first time that element enters the viewport (via IntersectionObserver).
 *
 * Safe to use in lists/multi-scenario layouts where siblings may be rendered but off-screen.
 */
export const useOnceInView = (onView: () => void): ((node: HTMLElement | null) => void) => {
    const hasViewedRef = useRef(false);
    const observerRef = useRef<IntersectionObserver | null>(null);

    return useCallback(
        (node: HTMLElement | null) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            if (!node || hasViewedRef.current) return;

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && !hasViewedRef.current) {
                        hasViewedRef.current = true;
                        observerRef.current!.disconnect();
                        onView();
                    }
                },
                { threshold: 0.5 }
            );

            observerRef.current.observe(node);
        },
        [onView]
    );
};
